import { ExistingBookingSpikedTireSeasonBanner } from '@components/BookingFlow/Common/MobileBookingSpikedTireSeasonBanner';
import { TimeWindowModal } from '@components/BookingFlow/Mobile/TimeWindows/TimeWindowModal';
import { CustomerTimePickerNotices } from '@components/Common/CustomerTimePickerNotices';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { ContentWrapperCustomerApp } from '@components/Layouts/ContentWrapperCustomerApp';
import { NoddiLoadingScreenWithTrans } from '@components/Loading/NoddiLoadingScreenWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingLocationType, BookingRecordDetailsCustomer, DeliveryWindowOutputRecord } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { NoddiAsyncError } from 'noddi-types';
import { ApiErrorMessageInternal, FieldErrorMessage, NoddiButton, noddiToast } from 'noddi-ui-web';
import { BookingDetailsTabs, customerRoutes } from 'noddi-util';
import {
  BookingTimeWindowPicker,
  customerWeekCapacityRange,
  findSlotIdForTime,
  StationaryTimeSlotPicker,
  tracking,
  useAuthContext,
  useShowSlotAvailability,
  useStationaryCapacityRange
} from 'noddi-web';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { getCommonTranslations } from '../../../../utils/commonTranslations';
import { EditBookingTimeWindowDialog } from './EditBookingTimeWindowDialog';

export const EditBookingTimeWindow = () => {
  const { slug } = useParams();

  const {
    data: booking,
    isPending: isBookingPending,
    error: bookingError
  } = noddiAsync.useGet({
    type: URLKeys.getBookingInfo,
    input: { slug: slug! }
  });

  if (isBookingPending) {
    return <NoddiLoadingScreenWithTrans />;
  }
  if (bookingError) {
    return <ApiErrorPageWithTrans error={[bookingError]} />;
  }

  if (booking.locationType === BookingLocationType.STATIONARY) {
    return <EditBookingStationaryTimeWindowContent booking={booking} />;
  }

  return <EditBookingTimeWindowContent booking={booking} />;
};

type ContentProps = { booking: BookingRecordDetailsCustomer };

type EditBookingStationaryTimeWindowErrorProps = {
  error?: NoddiAsyncError;
  errorMessage?: string;
};

const EditBookingStationaryTimeWindowError = ({ error, errorMessage }: EditBookingStationaryTimeWindowErrorProps) => {
  if (error) {
    return <ApiErrorMessageInternal error={error} />;
  }
  if (errorMessage) {
    return <FieldErrorMessage errorMessage={errorMessage} />;
  }
  return null;
};

const EditBookingStationaryTimeWindowContent = ({ booking }: ContentProps) => {
  const navigate = useNavigate();
  const showSlotAvailability = useShowSlotAvailability();
  const [selectedTimeWindow, setSelectedTimeWindow] = useState<{ startsAt: string; endsAt: string } | null>(null);

  // Primary sales item comes from the booking's own order lines (core data).
  // Capacity needs the sales item id, so keep any line that has one.
  const primaryOrderLines = booking.bookingItemsCar
    .flatMap((car) => car.orderLines ?? [])
    .filter((line) => line.salesItemId != null);

  const primarySalesItemId = primaryOrderLines[0]?.salesItemId ?? undefined;
  const garageId = booking.garageId ?? undefined;

  // Select-only: capacity week without hold (edit booking patches times directly).
  const {
    days,
    slots,
    slotLookup,
    goToPreviousRange,
    goToNextRange,
    isPreviousDisabled,
    isNextDisabled,
    dateRangeLabel,
    isPending,
    error: timeSlotPickerError
  } = useStationaryCapacityRange({
    garageId,
    salesItemIds: primarySalesItemId != null ? [primarySalesItemId] : undefined,
    detailed: showSlotAvailability,
    enabled: garageId != null && primarySalesItemId != null,
    range: customerWeekCapacityRange(14),
    // The patch below books the chosen window, so it must still be free when tapped.
    keepFresh: true
  });

  const translations = {
    previous: t`Previous Week`,
    next: t`Next Week`,
    emptyState: t`No time slots available for this week`
  };

  const selectedSlotId = findSlotIdForTime(slots, selectedTimeWindow);

  const handleSelectSlot = (timeSlot: { id: string }) => {
    const slot = slotLookup.get(timeSlot.id);
    if (!slot || selectedSlotId === slot.id) {
      return;
    }
    setSelectedTimeWindow({ startsAt: slot.start, endsAt: slot.end });
  };

  const { mutateAsync: updateBooking, isPending: isUpdating } = noddiAsync.usePatch({
    type: URLKeys.patchBooking,
    queryConfig: {
      onSuccess: async () => {
        await invalidateQueryExactMatch({
          urlKey: URLKeys.getBookingInfo,
          input: { slug: booking.slug }
        });
        noddiToast.success(t`Booking time changed successfully`);
        navigate(customerRoutes.bookingInfo.getPath({ slug: booking.slug, tab: BookingDetailsTabs.ORDER_DETAILS }));
      }
    }
  });

  const handleConfirm = async () => {
    if (!selectedTimeWindow) {
      return;
    }

    await updateBooking({
      bookingId: booking.id,
      customStartTime: selectedTimeWindow.startsAt,
      customEndTime: selectedTimeWindow.endsAt
    });
  };

  const canConfirm = !!selectedTimeWindow && !!primarySalesItemId && garageId != null && !isUpdating;
  const hasNoServiceForBooking = primaryOrderLines.length === 0;
  const errorMessage = hasNoServiceForBooking
    ? t`No service found for this booking.`
    : garageId == null
      ? t`Could not map booking location to available time slots.`
      : undefined;
  const error: NoddiAsyncError | undefined = !errorMessage ? (timeSlotPickerError ?? undefined) : undefined;
  const hasError = error != null || errorMessage != null;

  return (
    <ContentWrapperCustomerApp title={t`Select a time slot`}>
      <div className='flex flex-col gap-4'>
        <ExistingBookingSpikedTireSeasonBanner
          booking={booking}
          bookingDate={selectedTimeWindow?.startsAt ?? booking.date}
        />
        <CustomerTimePickerNotices surface='stationary' serviceDepartmentId={booking.serviceDepartmentId} />
        {hasError ? (
          <EditBookingStationaryTimeWindowError error={error} errorMessage={errorMessage} />
        ) : (
          <>
            <StationaryTimeSlotPicker
              days={days}
              selectedSlotId={selectedSlotId}
              onSelectSlot={handleSelectSlot}
              onNavigatePrevious={goToPreviousRange}
              onNavigateNext={goToNextRange}
              isPreviousDisabled={isPreviousDisabled}
              isNextDisabled={isNextDisabled}
              dateRangeLabel={dateRangeLabel}
              isPending={isPending}
              showAvailableCount={showSlotAvailability}
              translations={translations}
            />
            <div className='flex justify-end'>
              {/* oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs handleConfirm(); React discards the returned promise, so a rejection is unhandled unless the body catches */}
              <NoddiButton onClick={handleConfirm} loading={isUpdating} disabled={!canConfirm}>
                <Trans>Confirm slot</Trans>
              </NoddiButton>
            </div>
          </>
        )}
      </div>
    </ContentWrapperCustomerApp>
  );
};

const EditBookingTimeWindowContent = ({ booking }: ContentProps) => {
  const { isSuperUser } = useAuthContext();
  const { currentBrand } = useCurrentBrand();

  const [editDialogIsOpen, setEditDialogIsOpen] = useState(false);
  const [selectedTimeWindow, setSelectedTimeWindow] = useState<DeliveryWindowOutputRecord>();
  const [showExtraInfoModal, setShowExtraInfoModal] = useState(false);

  const {
    isPending: isBookingPermissionsPending,
    data: bookingPermissions,
    error: bookingPermissionsError
  } = noddiAsync.useGet({
    type: URLKeys.getEditBookingPermissions,
    input: { bookingId: booking.id }
  });

  if (isBookingPermissionsPending) {
    return <NoddiLoadingScreenWithTrans />;
  }
  if (bookingPermissionsError) {
    return <ApiErrorPageWithTrans error={[bookingPermissionsError]} />;
  }

  const { address, bookingItemsCar, deliveryWindowStartsAt, deliveryWindowEndsAt } = booking;

  return (
    <ContentWrapperCustomerApp title={t`When is it convenient for us to come to you?`}>
      <EditBookingTimeWindowDialog
        bookingId={booking.id}
        isOpen={editDialogIsOpen}
        setIsOpen={setEditDialogIsOpen}
        bookingSlug={booking.slug}
        selectedTimeWindow={selectedTimeWindow}
      />
      <TimeWindowModal open={showExtraInfoModal} onClose={() => setShowExtraInfoModal(false)} />
      <ExistingBookingSpikedTireSeasonBanner
        booking={booking}
        bookingDate={selectedTimeWindow?.startsAt ?? booking.date}
      />
      <CustomerTimePickerNotices surface='mobile' serviceDepartmentId={booking.serviceDepartmentId} />
      <BookingTimeWindowPicker
        address={address}
        brandDomain={currentBrand}
        surface='page'
        cars={bookingItemsCar.map((car) => ({
          licensePlate: car.licensePlate,
          selectedSalesItemIds: car.orderLines?.map((orderLine) => orderLine.salesItemId!) ?? []
        }))}
        selectedDeliveryWindow={
          deliveryWindowStartsAt && deliveryWindowEndsAt
            ? { startsAt: deliveryWindowStartsAt, endsAt: deliveryWindowEndsAt }
            : undefined
        }
        onAvailableSelect={(timeWindow) => {
          if (bookingPermissions.timeWindows.canEdit || isSuperUser) {
            setSelectedTimeWindow(timeWindow);
            setEditDialogIsOpen(true);
          }
        }}
        onUnavailableSelect={(timeWindow) => {
          if (!isSuperUser) {
            return;
          }
          setSelectedTimeWindow(timeWindow);
          setEditDialogIsOpen(true);
        }}
        translations={getCommonTranslations().timePicker}
        customButton={
          <NoddiButton
            className='h-9 md:h-8'
            size='small'
            startIcon='Info'
            onClick={() => {
              setShowExtraInfoModal(true);
              tracking.track('clickedReadMoreAboutDeliveryTimePicker');
            }}
            variant='outline'
          >
            <Trans>About delivery</Trans>
          </NoddiButton>
        }
      />
    </ContentWrapperCustomerApp>
  );
};
